"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface ChipNumberInputProps {
  chipNumber: string;
  onChipNumberChange: (value: string) => void;
  error?: string;
  isLoading?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  showHint?: boolean;
}

const ChipNumberInput: React.FC<ChipNumberInputProps> = ({
  chipNumber,
  onChipNumberChange,
  error,
  isLoading = false,
  inputRef,
  showHint = true,
}) => {
  const { t } = useTranslation();
  const labelKey = "petDetails.chipNumberLabelOptional";

  const handleChange = (value: string) => {
    const sanitized = value.replace(/\D/g, "").slice(0, 15);
    onChipNumberChange(sanitized);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="chip-number">{t(labelKey)}</Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4 text-gray-500" />
              <span className="sr-only">{t("common.info")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("petDetails.chipNumberInfoTitle")}</DialogTitle>
              <DialogDescription className="whitespace-pre-line">
                {t("petDetails.chipNumberInfoContent")}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <Input
        id="chip-number"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={15}
        value={chipNumber}
        onChange={(e) => handleChange(e.target.value)}
        disabled={isLoading}
        className={cn(error && "border-destructive focus-visible:ring-destructive")}
        ref={inputRef}
      />
      {showHint && (
        <p className="text-sm text-muted-foreground">{t("petDetails.chipNumberHint")}</p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default ChipNumberInput;
